import React, { FC, ReactNode, useEffect } from 'react';
import { useUserContext } from 'src/context/user';
import { ROUTES } from 'src/constants/routes.const';
import { convertToMemriseError } from 'src/utils/errors';
import { makeUrlRelativeOrDashboard } from 'src/utils/location';
import useSafePush from 'src/hooks/useSafePush';
import { isReplitUrl } from 'src/utils/replit/isReplitUrl';
import * as S from './styles';

interface Props {
  slug?: string;
  children?: ReactNode;
}

const RedirectIfAuth: FC<Props> = ({ slug = ROUTES.dashboard, children }) => {
  const { error, user } = useUserContext();
  const shouldRedirect = !!user;

  const { safePush } = useSafePush();

  useEffect(() => {
    const handleRedirect = async () => {
      if (shouldRedirect) {
        // Check if this is a Replit URL
        if (slug && isReplitUrl(slug)) {
          // Redirect to interstitial page where user can click to open Replit
          // This ensures we have a user action for popup, bypassing popup blockers
          await safePush(`/replit-redirect?url=${encodeURIComponent(slug)}`);
        } else {
          await safePush(makeUrlRelativeOrDashboard(slug));
        }
      }
    };

    void handleRedirect();
  }, [slug, shouldRedirect, safePush]);

  // If there is an error, silently log with console.error
  // but show children
  useEffect(() => {
    if (error) {
      const memriseError = convertToMemriseError(error);
      const unauthorized = 401;
      if (memriseError.httpStatusCode !== unauthorized) {
        console.error(error);
      }
    }
  }, [error]);

  if (!error && shouldRedirect) {
    return (
      <S.Root>
        <S.Loader />
      </S.Root>
    );
  }

  return <>{children}</>;
};

export default RedirectIfAuth;
